import React, { useState, useEffect, useCallback, useRef } from "react";
import { useApi, apiPost, apiPut } from "../hooks/useApi.ts";

interface Project {
  id: number;
  name: string;
}

interface Suite {
  id: number;
  projectId: number;
  storefrontUrl: string;
  platform: "shopify" | "bigcommerce";
  enabled: boolean;
  scheduleCron: string;
  runInGithub: boolean;
  targetBranch: string;
  webhookToken: string;
  lastGeneratedAt: string | null;
  lastRunAt: string | null;
  repo: { owner: string; name: string } | null;
  checklistCount: number;
  testCount: number;
  latestRun: QARun | null;
  generationStatus: "idle" | "generating" | "error";
  generationError: string | null;
  generationWarnings: string[];
}

interface QARun {
  id: number;
  status: string;
  trigger: string;
  total: number;
  passed: number;
  failed: number;
  started_at: string;
  finished_at: string | null;
  error_message: string | null;
}

interface QAResult {
  id: number;
  test_name: string;
  status: string;
  duration_ms: number;
  error_message: string | null;
  github_issue_number: number | null;
}

interface ChecklistItem {
  id: number;
  category: string;
  title: string;
  description: string;
  source: string;
}

interface QATest {
  id: number;
  name: string;
  file_path: string;
  spec_code: string;
}

interface GhPermissions {
  hasToken: boolean;
  valid?: boolean;
  tokenType?: "classic" | "fine-grained";
  scopes?: string[];
  missing?: string[];
  required?: string[];
  ok?: boolean | null;
}

type Tab = "checklist" | "tests" | "runs";

export function QAAutomation() {
  const { data: projects } = useApi<Project[]>("/api/projects");
  const [projectId, setProjectId] = useState<number>(0);
  const [suite, setSuite] = useState<Suite | null>(null);
  const [loadingSuite, setLoadingSuite] = useState(false);
  const [tab, setTab] = useState<Tab>("checklist");

  // editable config
  const [storefrontUrl, setStorefrontUrl] = useState("");
  const [platform, setPlatform] = useState<"shopify" | "bigcommerce">("shopify");
  const [scheduleCron, setScheduleCron] = useState("0 6 * * *");
  const [runInGithub, setRunInGithub] = useState(true);
  const [targetBranch, setTargetBranch] = useState("");
  const [enabled, setEnabled] = useState(false);

  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [jobEnabled, setJobEnabled] = useState<boolean | null>(null);
  const [ghPerm, setGhPerm] = useState<GhPermissions | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showConfig, setShowConfig] = useState(false);
  const [activeRun, setActiveRun] = useState<QARun | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Tick an elapsed-seconds counter while a run or generation is in progress.
  useEffect(() => {
    if (busy !== "run" && busy !== "generate") return;
    setElapsed(0);
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [busy]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Poll the suite until background generation finishes, then refresh the tabs.
  const pollGeneration = useCallback(
    (pid: number) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const res = await fetch(`/api/projects/${pid}/qa`);
          const data = (await res.json()) as Suite | null;
          if (!data) return;
          setSuite(data);
          if (data.generationStatus !== "generating") {
            stopPolling();
            setBusy(null);
            setRefreshKey((k) => k + 1);
            if (data.generationStatus === "error") {
              setMsg(`Generation failed: ${data.generationError}`);
            } else {
              let m = `Generated ${data.checklistCount} checklist items and ${data.testCount} tests.`;
              if (data.generationWarnings?.length) m += ` Warnings: ${data.generationWarnings.join("; ")}`;
              setMsg(m);
            }
          }
        } catch {
          /* keep polling; transient errors are fine */
        }
      }, 3000);
    },
    [stopPolling]
  );

  // Poll a single run until it finishes, then refresh the suite + Runs tab.
  const pollRun = useCallback(
    (runId: number, pid: number) => {
      stopPolling();
      const tick = async () => {
        try {
          const res = await fetch(`/api/qa/runs/${runId}`);
          if (!res.ok) return;
          const data = (await res.json()) as { run: QARun };
          if (!data.run) return;
          setActiveRun(data.run); // live status every tick
          if (data.run.status !== "running") {
            stopPolling();
            setBusy(null);
            await loadSuite(pid);
            setRefreshKey((k) => k + 1);
            const r = data.run;
            let m = `Run #${r.id}: ${r.status} — ${r.passed}/${r.total} passed.`;
            if (r.error_message) m += ` ${r.error_message}`;
            setMsg(m);
          }
        } catch {
          /* keep polling through transient errors */
        }
      };
      void tick(); // immediate first check, then every 3s
      pollRef.current = setInterval(tick, 3000);
    },
    [stopPolling]
  );

  // Clean up the poller when leaving the page.
  useEffect(() => stopPolling, [stopPolling]);

  const loadSuite = useCallback(async (pid: number) => {
    setLoadingSuite(true);
    setSuite(null);
    setActiveRun(null);
    try {
      const res = await fetch(`/api/projects/${pid}/qa`);
      const data = (await res.json()) as Suite | null;
      setSuite(data);
      // Show the config form only until a suite has been configured; once it
      // has a storefront URL, collapse it behind the gear icon.
      setShowConfig(!data || !data.storefrontUrl);
      if (data) {
        setStorefrontUrl(data.storefrontUrl);
        setPlatform(data.platform);
        setScheduleCron(data.scheduleCron);
        setRunInGithub(data.runInGithub);
        setTargetBranch(data.targetBranch || "");
        setEnabled(data.enabled);
        // Resume polling if a background generation is still running.
        if (data.generationStatus === "generating") {
          setBusy("generate");
          setMsg("Generating… this can take a minute.");
          pollGeneration(pid);
        }
      } else {
        setStorefrontUrl("");
        setPlatform("shopify");
        setScheduleCron("0 6 * * *");
        setRunInGithub(true);
        setTargetBranch("");
        setEnabled(false);
      }
    } finally {
      setLoadingSuite(false);
    }
  }, [pollGeneration]);

  useEffect(() => {
    if (projectId) loadSuite(projectId);
  }, [projectId, loadSuite]);

  // Load whether the scheduled qa-automation job is enabled for this user.
  useEffect(() => {
    fetch("/api/jobs")
      .then((r) => r.json())
      .then((jobs: Array<{ name: string; enabled: boolean }>) => {
        const j = jobs.find((x) => x.name === "qa-automation");
        setJobEnabled(j ? j.enabled : false);
      })
      .catch(() => setJobEnabled(false));
  }, []);

  // Check whether the GitHub token has the scopes QA needs.
  useEffect(() => {
    fetch("/api/qa/github-permissions")
      .then((r) => r.json())
      .then((data: GhPermissions) => setGhPerm(data))
      .catch(() => setGhPerm(null));
  }, []);

  const saveConfig = async () => {
    setBusy("save");
    setMsg(null);
    try {
      const updated = await apiPut<Suite>(`/api/projects/${projectId}/qa`, {
        storefrontUrl,
        platform,
        scheduleCron,
        runInGithub,
        targetBranch,
        enabled,
      });
      setSuite(updated);
      setMsg("Configuration saved.");
      // Collapse the form once the suite is configured.
      if (updated.storefrontUrl) setShowConfig(false);
    } catch (err) {
      setMsg((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const generate = async () => {
    setBusy("generate");
    setMsg("Generating… this can take a minute.");
    try {
      // Kicks off background generation (202); we then poll for completion.
      await apiPost(`/api/projects/${projectId}/qa/generate`, {});
      pollGeneration(projectId);
    } catch (err) {
      setMsg((err as Error).message);
      setBusy(null);
    }
  };

  const runNow = async () => {
    setBusy("run");
    setActiveRun(null);
    setMsg("Running tests against the storefront… this can take a few minutes.");
    setTab("runs");
    try {
      // Kicks off the run in the background (202); we poll the run until done.
      const { runId } = await apiPost<{ runId: number; status: string }>(
        `/api/projects/${projectId}/qa/run`,
        {}
      );
      pollRun(runId, projectId);
    } catch (err) {
      setMsg((err as Error).message);
      setBusy(null);
    }
  };

  const toggleJob = async (next: boolean) => {
    setJobEnabled(next);
    try {
      await apiPut(`/api/jobs/qa-automation/enabled`, { enabled: next });
    } catch (err) {
      setMsg((err as Error).message);
      setJobEnabled(!next);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>QA Automation</h1>
        <p>Generate and run automated storefront QA from Teamwork history.</p>
      </div>

      <GitHubPermissionBanner perm={ghPerm} />

      <div className="card">
        <div className="card-header">
          <h2>Project</h2>
        </div>
        <div className="form-group" style={{ maxWidth: 420 }}>
          <label>Select a project</label>
          <select value={projectId} onChange={(e) => setProjectId(Number(e.target.value))}>
            <option value={0}>— Choose a project —</option>
            {(projects || []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
          <input
            type="checkbox"
            checked={jobEnabled ?? false}
            onChange={(e) => toggleJob(e.target.checked)}
          />
          Enable the scheduled QA job for my account (runs due suites hourly)
        </label>
      </div>

      {(busy === "run" || busy === "generate") && (
        <div className="card" style={{ borderLeft: "3px solid var(--color-warning, #f59e0b)", display: "flex", alignItems: "center", gap: 12 }}>
          <span className="spinner" aria-hidden />
          <div style={{ fontSize: 14 }}>
            {busy === "generate"
              ? `Generating checklist & tests… ${elapsed}s elapsed`
              : `Running ${suite?.testCount ?? ""} test file(s) against the storefront… ${elapsed}s elapsed`}
          </div>
        </div>
      )}

      {busy === null && msg && (
        <div className="card" style={{ borderLeft: "3px solid var(--color-primary, #6366f1)" }}>
          <div style={{ fontSize: 14 }}>{msg}</div>
        </div>
      )}

      {projectId > 0 && showConfig && (
        <>
          <div className="card">
            <div className="card-header">
              <h2>Suite Configuration</h2>
            </div>
            {loadingSuite ? (
              <div className="loading">Loading...</div>
            ) : (
              <>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <div className="form-group" style={{ flex: 2, minWidth: 280 }}>
                    <label>Storefront URL</label>
                    <input
                      type="text"
                      value={storefrontUrl}
                      onChange={(e) => setStorefrontUrl(e.target.value)}
                      placeholder="https://store.example.com"
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                    <label>Platform</label>
                    <select value={platform} onChange={(e) => setPlatform(e.target.value as "shopify" | "bigcommerce")}>
                      <option value="shopify">Shopify</option>
                      <option value="bigcommerce">BigCommerce</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                    <label>Schedule (cron, UTC)</label>
                    <input
                      type="text"
                      value={scheduleCron}
                      onChange={(e) => setScheduleCron(e.target.value)}
                      placeholder="0 6 * * *"
                    />
                  </div>
                </div>
                {runInGithub && (
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 4 }}>
                    <div className="form-group" style={{ flex: 1, minWidth: 260 }}>
                      <label>GitHub branch</label>
                      <input
                        type="text"
                        value={targetBranch}
                        onChange={(e) => setTargetBranch(e.target.value)}
                        placeholder="default branch"
                      />
                      <span style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 4, display: "block" }}>
                        Branch to commit tests + workflow to (created from the default branch if it doesn't
                        exist). Leave blank to use the repo default. Note: GitHub only runs <em>scheduled</em>{" "}
                        Actions from the default branch.
                      </span>
                    </div>
                  </div>
                )}
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginTop: 4 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                    Enabled (eligible for scheduled runs)
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input type="checkbox" checked={runInGithub} onChange={(e) => setRunInGithub(e.target.checked)} />
                    Commit tests + workflow to GitHub
                  </label>
                </div>

                {suite && (
                  <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 8 }}>
                    Repo: {suite.repo ? `${suite.repo.owner}/${suite.repo.name}` : "not linked (set in Settings → Projects)"}
                    {suite.repo ? ` (branch: ${suite.targetBranch || "default"})` : ""}
                    {" · "}Checklist: {suite.checklistCount} · Tests: {suite.testCount}
                    {suite.lastGeneratedAt ? ` · Generated ${suite.lastGeneratedAt}` : ""}
                    {runInGithub && suite.webhookToken && (
                      <div style={{ marginTop: 6 }}>
                        For GitHub Action runs, add a repo secret <code>HERMES_QA_TOKEN</code> ={" "}
                        <code>{suite.webhookToken}</code>
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                  <button className="btn btn-primary btn-sm" onClick={saveConfig} disabled={busy !== null}>
                    {busy === "save" ? "Saving..." : "Save Config"}
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {projectId > 0 && (
        <>
          <div className="card">
            <div className="card-header">
              <h2>Test Suite</h2>
              <div className="qa-actions">
                {suite && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowConfig((s) => !s)}
                    title="Edit suite configuration"
                    aria-label="Edit suite configuration"
                  >
                    ⚙
                  </button>
                )}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={generate}
                  disabled={busy !== null || !suite?.storefrontUrl}
                  title={!suite?.storefrontUrl ? "Save the configuration first" : "Pull Teamwork history and (re)generate the checklist + tests"}
                >
                  {busy === "generate" ? "Generating..." : suite?.testCount ? "Regenerate Suite" : "Generate Suite"}
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={runNow}
                  disabled={busy !== null || !suite?.testCount}
                  title={!suite?.testCount ? "Generate the suite first" : "Run the tests against the storefront now"}
                >
                  {busy === "run" ? "Running..." : "Run Now"}
                </button>
              </div>
            </div>

            {!suite ? (
              <p style={{ color: "var(--color-text-muted)" }}>
                Save the configuration above to create the suite, then click <strong>Generate Suite</strong>.
              </p>
            ) : (
              <>
                <div className="qa-tabs">
                  <button className={`qa-tab ${tab === "checklist" ? "active" : ""}`} onClick={() => setTab("checklist")}>
                    Checklist{suite.checklistCount ? ` (${suite.checklistCount})` : ""}
                  </button>
                  <button className={`qa-tab ${tab === "tests" ? "active" : ""}`} onClick={() => setTab("tests")}>
                    Tests{suite.testCount ? ` (${suite.testCount})` : ""}
                  </button>
                  <button className={`qa-tab ${tab === "runs" ? "active" : ""}`} onClick={() => setTab("runs")}>
                    Runs
                  </button>
                </div>
                {tab === "checklist" && <ChecklistTab key={`c-${refreshKey}`} projectId={projectId} />}
                {tab === "tests" && <TestsTab key={`t-${refreshKey}`} projectId={projectId} />}
                {tab === "runs" && (
                  <RunsTab key={`r-${refreshKey}`} runId={activeRun?.id ?? suite.latestRun?.id ?? null} />
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Banner({ tone, title, children }: { tone: "danger" | "warn" | "info" | "ok"; title: string; children?: React.ReactNode }) {
  const color =
    tone === "danger" ? "#ef4444" : tone === "warn" ? "#f59e0b" : tone === "ok" ? "#22c55e" : "#6366f1";
  return (
    <div className="card" style={{ borderLeft: `3px solid ${color}` }}>
      <strong style={{ color }}>{title}</strong>
      {children && <div style={{ fontSize: 13, marginTop: 6 }}>{children}</div>}
    </div>
  );
}

const SETTINGS_HINT = (
  <>
    Update it in <a href="#settings">Settings → API Keys → GitHub Token</a>.
  </>
);

const CLASSIC_STEPS = (
  <ol style={{ margin: "6px 0 0", paddingLeft: 18 }}>
    <li>
      Open GitHub → <em>Settings → Developer settings → Personal access tokens → Tokens (classic)</em>.
    </li>
    <li>Edit your token (or generate a new one) and enable the <code>repo</code> and <code>workflow</code> scopes.</li>
    <li>Copy the token and paste it into {SETTINGS_HINT}</li>
  </ol>
);

function GitHubPermissionBanner({ perm }: { perm: GhPermissions | null }) {
  if (!perm) return null;

  if (!perm.hasToken) {
    return (
      <Banner tone="danger" title="No GitHub token configured">
        QA can't commit tests, run the GitHub Action, or open issues without one. Add a token with the{" "}
        <code>repo</code> and <code>workflow</code> scopes. {SETTINGS_HINT}
      </Banner>
    );
  }

  if (perm.valid === false) {
    return (
      <Banner tone="danger" title="GitHub token is invalid or expired">
        Hermes couldn't authenticate with your GitHub token. {SETTINGS_HINT}
      </Banner>
    );
  }

  if (perm.tokenType === "classic" && perm.missing && perm.missing.length > 0) {
    return (
      <Banner tone="danger" title={`GitHub token is missing required scope(s): ${perm.missing.join(", ")}`}>
        QA needs <code>repo</code> (commit tests &amp; open issues) and <code>workflow</code> (write the GitHub
        Actions file).
        {CLASSIC_STEPS}
      </Banner>
    );
  }

  if (perm.tokenType === "fine-grained") {
    return (
      <Banner tone="info" title="Fine-grained GitHub token — couldn't auto-verify permissions">
        Make sure this token grants the linked repository <strong>Contents</strong>, <strong>Issues</strong>, and{" "}
        <strong>Workflows</strong> = <em>Read and write</em>. {SETTINGS_HINT}
      </Banner>
    );
  }

  if (perm.ok) {
    return <Banner tone="ok" title="GitHub token has the required QA permissions" />;
  }

  return null;
}

function ChecklistTab({ projectId }: { projectId: number }) {
  const { data, loading } = useApi<ChecklistItem[]>(`/api/projects/${projectId}/qa/checklist`);
  if (loading) return <div className="loading">Loading...</div>;
  if (!data || data.length === 0) return <p>No checklist yet. Click “Generate Suite”.</p>;
  return (
    <table>
      <thead>
        <tr>
          <th>Category</th>
          <th>Requirement</th>
          <th>Source</th>
        </tr>
      </thead>
      <tbody>
        {data.map((it) => (
          <tr key={it.id}>
            <td>{it.category}</td>
            <td>
              <strong>{it.title}</strong>
              {it.description && <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{it.description}</div>}
            </td>
            <td>{it.source}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TestsTab({ projectId }: { projectId: number }) {
  const { data, loading } = useApi<QATest[]>(`/api/projects/${projectId}/qa/tests`);
  const [open, setOpen] = useState<number | null>(null);
  if (loading) return <div className="loading">Loading...</div>;
  if (!data || data.length === 0) return <p>No tests generated yet.</p>;
  return (
    <div>
      {data.map((t) => (
        <div key={t.id} style={{ marginBottom: 8 }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setOpen(open === t.id ? null : t.id)}
          >
            {open === t.id ? "▾" : "▸"} {t.file_path} — {t.name}
          </button>
          {open === t.id && (
            <pre style={{ background: "var(--color-bg-alt, #0f172a11)", padding: 12, overflowX: "auto", fontSize: 12, borderRadius: 6 }}>
              {t.spec_code}
            </pre>
          )}
        </div>
      ))}
    </div>
  );
}

function RunsTab({ runId }: { runId: number | null }) {
  const { data, loading } = useApi<{ run: QARun; results: QAResult[] }>(
    runId ? `/api/qa/runs/${runId}` : "/api/health"
  );
  if (!runId) return <p>No runs yet. Click “Run Now”.</p>;
  if (loading) return <div className="loading">Loading...</div>;
  if (!data || !data.run) return <p>No run data.</p>;

  const { run, results } = data;
  return (
    <div>
      <p>
        <strong>Run #{run.id}</strong> ({run.trigger}) — status: {run.status} · {run.passed}/{run.total} passed
        {run.error_message ? ` · ${run.error_message}` : ""}
      </p>
      {results.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Test</th>
              <th>Status</th>
              <th>Duration</th>
              <th>Issue</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r) => (
              <tr key={r.id}>
                <td>
                  {r.test_name}
                  {r.error_message && r.status === "failed" && (
                    <div style={{ fontSize: 12, color: "var(--color-danger, #ef4444)" }}>{r.error_message}</div>
                  )}
                </td>
                <td>
                  <span className={`status-dot ${r.status === "passed" ? "connected" : "disconnected"}`} /> {r.status}
                </td>
                <td>{Math.round(r.duration_ms)}ms</td>
                <td>{r.github_issue_number ? `#${r.github_issue_number}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
